import React, { useEffect, useState } from 'react'
import AnimatedBackgroundContainer from 'components/common/AnimatedBackgroundContainer'
import styles from 'styles/pages/AcceptInvitePage.module.sass'
import zeroneLogoDark from 'assets/images/svg/logo-dark.svg'
import { Link, useNavigate } from 'react-router-dom'
import PrimaryButton from 'components/common/PrimaryButton'
import SecondaryButton from 'components/common/SecondaryButton'
import ZeroneLoader from 'components/common/ZeroneLoader'
import { acceptOrgInvite, declineOrgInvite, fetchPendingInvites } from 'api/TeamApiService'
import { useToast } from 'provider/ToastProvider'

export interface PendingInvite {
    id: string
    orgOwnerName: string
    planName: string
}

/**
 * AcceptInvitePage — shown after an invited user creates their free account.
 * Fetches any pending org invites for the current user and presents Accept / Decline.
 */
const AcceptInvitePage: React.FC = () => {
    const navigate = useNavigate()
    const { addToast } = useToast()

    const [loading, setLoading] = useState(true)
    const [acting, setActing] = useState(false)
    const [invites, setInvites] = useState<PendingInvite[]>([])

    useEffect(() => {
        fetchPendingInvites()
            .then((result) => setInvites(result))
            .catch(() => setInvites([]))
            .finally(() => setLoading(false))
    }, [])

    const handleAccept = async (inviteId: string) => {
        setActing(true)
        const ok = await acceptOrgInvite(inviteId)
        setActing(false)
        if (ok) {
            addToast({ type: 'success', message: 'Invite accepted! You now have workspace access.' })
            navigate('/success', {
                state: {
                    kind: 'invite-accepted',
                    title: 'You\'re in!',
                    message: 'Your invite has been accepted. You can now access the workspace via the Workspace page.',
                    redirectTo: '/workspace',
                    messageType: 'success',
                }
            })
        } else {
            addToast({ type: 'error', message: 'Could not accept invite. Please try again.' })
        }
    }

    const handleDecline = async (inviteId: string) => {
        setActing(true)
        await declineOrgInvite(inviteId)
        setActing(false)
        addToast({ type: 'info', message: 'Invite declined. You can still use your free account.' })
        navigate('/success', {
            state: {
                kind: 'sign-up-complete',
                title: 'Account Created!',
                message: 'Your free account is ready. You can explore ZeroneAnalytiQs and upgrade anytime.',
                redirectTo: '/',
                messageType: 'success',
            }
        })
    }

    const handleSkip = () => {
        navigate('/success', {
            state: {
                kind: 'sign-up-complete',
                title: 'Account Created!',
                message: 'Your free account is ready. Welcome to ZeroneAnalytiQs!',
                redirectTo: '/',
                messageType: 'success',
            }
        })
    }

    return (
        <div className={styles['accept-invite-page']}>
            <AnimatedBackgroundContainer showFooter={false}>
                <div className={styles['card']}>
                    <div className={styles['logo-wrapper']}>
                        <Link to="/">
                            <img src={zeroneLogoDark} className={styles['logo']} alt="Zerone AnalytiQs Logo" />
                        </Link>
                    </div>

                    {loading ? (
                        <ZeroneLoader text="Checking your invites..." />
                    ) : invites.length === 0 ? (
                        <>
                            <h2 className={styles['title']}>Account Created!</h2>
                            <p className={styles['description']}>
                                Your free account is ready. No pending invites were found for your email.
                            </p>
                            <PrimaryButton
                                text="Go to Homepage"
                                primaryColor="#FF5522"
                                textColor="#FFFFFF"
                                hoverColor="#FFFFFF"
                                hoverTextColor="#FF5522"
                                doSomething={handleSkip}
                            />
                        </>
                    ) : (
                        <>
                            <h2 className={styles['title']}>You&apos;ve been invited!</h2>
                            {invites.map((invite) => (
                                <div key={invite.id} className={styles['invite-card']}>
                                    <p className={styles['description']}>
                                        <strong>{invite.orgOwnerName}</strong> has invited you to join their{' '}
                                        <strong>{invite.planName}</strong> workspace.
                                        Accept to gain workspace access, or decline to keep your free account only.
                                    </p>
                                    <div className={styles['actions']}>
                                        <PrimaryButton
                                            text={acting ? 'Please wait...' : 'Accept Invitation'}
                                            primaryColor="#FF5522"
                                            textColor="#FFFFFF"
                                            hoverColor="#FFFFFF"
                                            hoverTextColor="#FF5522"
                                            doSomething={() => { if (!acting) void handleAccept(invite.id) }}
                                        />
                                        <SecondaryButton
                                            text="Decline"
                                            primaryColor="transparent"
                                            textColor="#FF5522"
                                            hoverColor="#FF5522"
                                            hoverTextColor="#FFFFFF"
                                            doSomething={() => { if (!acting) void handleDecline(invite.id) }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </AnimatedBackgroundContainer>
        </div>
    )
}

export default AcceptInvitePage
